import React, { useState, useEffect, useRef } from 'react';
import { base44 } from '@/api/base44Client';
import { CheckCircle, AlertCircle, ChevronRight, ChevronLeft, RotateCcw, Circle, ShieldCheck, Clock } from 'lucide-react';
import { useNavigate, useBlocker, useBeforeUnload, useLocation } from 'react-router-dom';
import { assessmentTasks, taskProgressAnswer, PROGRESS_LABELS } from '@inclusivecore/shared';
import { useOrganisation } from '@/lib/OrganisationContext';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import QuestionCard from '@/components/assessment/QuestionCard';
import LevelAuthorisation from '@/components/assessment/LevelAuthorisation';
import StartAssessmentDialog from '@/components/assessment/StartAssessmentDialog';

const LEVEL_LABELS = { 1: 'Committed', 2: 'Employer', 3: 'Leader' };

export default function Assessment() {
  const { selectedOrg, registerOrganisationChangeGuard } = useOrganisation();
  const [questions, setQuestions] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [view, setView] = useState('overview');
  const [answerModels, setAnswerModels] = useState({});
  const [taskProgress, setTaskProgress] = useState({});
  const [draftDirty, setDraftDirty] = useState(false);
  const [saveError, setSaveError] = useState('');
  const saveTail = useRef(Promise.resolve());
  const saveRevision = useRef(0);
  const discardingNavigation = useRef(false);
  const [replacePrompt, setReplacePrompt] = useState(false);
  const [answers, setAnswers] = useState({}); // { [questionId]: ["criterion text", ...] }
  const [answerValues, setAnswerValues] = useState({}); // Saved answer values, including versioned self-report answers
  const [explicitResponses, setExplicitResponses] = useState({});
  const [responseTouched, setResponseTouched] = useState({});
  const [answerNotes, setAnswerNotes] = useState({});
  const [legacyQuestionIds, setLegacyQuestionIds] = useState([]);
  const [authorisations, setAuthorisations] = useState({ 1: null, 2: null, 3: null }); // { level: { by, at } | null }
  const [currentStep, setCurrentStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [assessmentId, setAssessmentId] = useState(null);
  const [assessmentStatus, setAssessmentStatus] = useState(null);
  const [reassessing, setReassessing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [actionAnnouncement, setActionAnnouncement] = useState('');
  const [invalidQuestionId, setInvalidQuestionId] = useState(null);
  const [taskGeneration, setTaskGeneration] = useState(null);
  const [retryingTasks, setRetryingTasks] = useState(false);
  const liveRef = useRef(null);
  const loadGeneration = useRef(0);
  const actionRef = useRef(null);
  const levelHeadingRef = useRef(null);
  const resultHeadingRef = useRef(null);
  const overviewHeadingRef = useRef(null);
  const replaceTriggerRef = useRef(null);
  const lastLoad = useRef({ requestedId: null, nextView: 'overview' });
  const navigationIntent = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();
  const reviewId = new URLSearchParams(location.search).get('review');
  // Query dialogs keep this editor mounted, including its unsaved progress.
  const blocker = useBlocker(React.useCallback(({ currentLocation, nextLocation }) =>
    draftDirty && (currentLocation.pathname !== nextLocation.pathname || new URLSearchParams(currentLocation.search).get('review') !== new URLSearchParams(nextLocation.search).get('review')), [draftDirty]));
  useBeforeUnload(React.useCallback(event => {
    if (draftDirty) { event.preventDefault(); event.returnValue = ''; }
  }, [draftDirty]));
  useEffect(() => { if (blocker.state === 'blocked' && !draftDirty) blocker.proceed(); }, [blocker, draftDirty]);

  const levels = [1, 2, 3];
  const currentLevel = levels[currentStep];

  useEffect(() => {
    if (loading || loadError) return;
    const frame = requestAnimationFrame(() => {
      if (view === 'overview') overviewHeadingRef.current?.focus();
      else if (result || view === 'review') resultHeadingRef.current?.focus();
      else levelHeadingRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [view, loading, loadError, assessmentId, result?.id]);

  useEffect(() => {
    if (!invalidQuestionId) return;
    requestAnimationFrame(() => {
      const fieldset = document.getElementById(`assessment-question-${invalidQuestionId}`);
      fieldset?.scrollIntoView({ block: 'center' });
      const unanswered = [...(fieldset?.querySelectorAll('fieldset') || [])].find(group => !group.querySelector('input:checked'));
      (unanswered || fieldset)?.querySelector('input')?.focus();
      setInvalidQuestionId(null);
    });
  }, [invalidQuestionId, currentStep]);

  const orgId = selectedOrg?.id;
  useEffect(() => {
    const generation = ++loadGeneration.current;
    actionRef.current = null;
    setQuestions([]); setAssessments([]); setView('overview'); setAnswerModels({}); setTaskProgress({}); setReplacePrompt(false);
    setDraftDirty(false); setSaveError(''); saveRevision.current += 1;
    setAnswers({});
    setAnswerValues({});
    setExplicitResponses({});
    setResponseTouched({});
    setAnswerNotes({});
    setLegacyQuestionIds([]);
    setSaving(false); setTransitioning(false);
    setSubmitting(false);
    setReassessing(false);
    setRequestingLevel(null);
    setAuthorisations({ 1: null, 2: null, 3: null });
    setAssessmentId(null);
    setAssessmentStatus(null);
    setCurrentStep(0);
    setResult(null);
    setLoadError('');
    setActionError('');
    setActionAnnouncement('');
    setInvalidQuestionId(null);
    setTaskGeneration(null);
    setRetryingTasks(false);
    const intent = navigationIntent.current;
    navigationIntent.current = null;
    const matches = intent?.orgId === orgId && intent?.reviewId === reviewId;
    loadQuestions(generation, matches ? intent.requestedId : reviewId, matches ? intent.nextView : reviewId ? 'review' : 'overview');
    return () => { loadGeneration.current += 1; };
  }, [orgId, reviewId]);

  function openAssessment(requestedId = null, nextView = 'overview') {
    const nextReview = nextView === 'review' ? requestedId : null;
    if (reviewId === nextReview) return loadQuestions(loadGeneration.current, requestedId, nextView);
    navigationIntent.current = { orgId, reviewId: nextReview, requestedId, nextView };
    const search = new URLSearchParams(location.search);
    if (nextReview) search.set('review', nextReview);
    else search.delete('review');
    navigate({ pathname: location.pathname, search: search.toString(), hash: location.hash });
  }

  async function loadQuestions(generation = loadGeneration.current, requestedId = null, nextView = 'overview') {
    lastLoad.current = { requestedId, nextView };
    setLoading(true); setLoadError('');
    try {
      const recent = await base44.entities.Assessment.filter(orgId ? { organisation_id: orgId } : {}, '-created_date', 100);
      if (generation !== loadGeneration.current) return;
      const open = recent.find(a => a.status === 'in_progress');
      setAssessments(recent);
      const current = requestedId ? await base44.entities.Assessment.get(requestedId) : open ?? recent.find(a => a.status === 'completed') ?? null;
      if (generation !== loadGeneration.current) return;
      setView(nextView); setActionAnnouncement('');
      if (!current) { setQuestions([]); setResult(null); setAssessmentId(null); setAssessmentStatus(null); return; }
      if (generation !== loadGeneration.current) return;

      const pinnedId = current.framework_version_id;
      if (!pinnedId) throw new Error('This assessment has no saved framework version and cannot be displayed safely');
      let qs = await base44.framework.questions(pinnedId);
      if (generation !== loadGeneration.current) return;
      if (!Array.isArray(qs)) qs = qs?.questions || [];
      if (qs.length === 0) throw new Error('No published assessment framework is available');
      setQuestions(qs);
      setAssessmentId(current.id);
      setAssessmentStatus(current.status);
      const restored = {};
      Object.entries(current.answers || {}).forEach(([qId, val]) => {
        if (val && typeof val === 'object' && Array.isArray(val.checked)) restored[qId] = val.checked;
        else if (Array.isArray(val)) restored[qId] = val;
      });
      setAnswers(restored);
      const restoredAnswers = {};
      Object.entries(current.answers || {}).forEach(([qId, val]) => { if (val && typeof val === 'object' && typeof val.answer === 'string') restoredAnswers[qId] = val.answer; });
      setAnswerValues(restoredAnswers);
      setTaskProgress(Object.fromEntries(Object.entries(current.answers || {}).filter(([, v]) => v?.model === 'task_progress_v1').map(([id, v]) => [id, v.tasks])));
      setDraftDirty(false); setSaveError('');
      setAnswerModels(Object.fromEntries(Object.entries(current.answers || {}).filter(([, v]) => v?.model).map(([id, v]) => [id, v.model])));
      const restoredExplicit = {};
      const restoredNotes = {};
      const legacy = [];
      Object.entries(current.answers || {}).forEach(([qId, val]) => {
        if (!val || typeof val !== 'object') return;
        if (typeof val.explicit === 'boolean') restoredExplicit[qId] = val.explicit;
        else if (typeof val.answer === 'string') legacy.push(qId);
        if (typeof val.notes === 'string') restoredNotes[qId] = val.notes;
      });
      setExplicitResponses(restoredExplicit);
      setResponseTouched({});
      setAnswerNotes(restoredNotes);
      setLegacyQuestionIds(legacy);
      const savedAuths = current.level_authorisations || {};
      setAuthorisations({
        1: savedAuths[1] || savedAuths['1'] || null,
        2: savedAuths[2] || savedAuths['2'] || null,
        3: savedAuths[3] || savedAuths['3'] || null,
      });
      setResult(current.status !== 'in_progress' ? current : null);
      if (current.status === 'completed') {
        try {
          const taskStatus = await base44.functions.assessmentEvidenceTasks(current.id);
          if (generation === loadGeneration.current) setTaskGeneration(taskStatus);
        } catch { if (generation === loadGeneration.current) setTaskGeneration({ status: 'pending' }); }
      }
    } catch (e) {
      if (generation !== loadGeneration.current) return;
      setQuestions([]);
      setLoadError(e.message || 'Unable to load the published assessment framework');
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }

  async function startReassessment(replace = false, startMode) {
    if (actionRef.current || reassessing) return;
    const token = { generation: loadGeneration.current, orgId };
    actionRef.current = token; setReassessing(true); setActionError('');
    try {
      if (startMode && draftDirty && !(await saveDraft())) throw new Error('Your progress could not be saved. Try again.');
      if (!currentAction(token)) return;
      const response = await base44.functions.invoke('startReassessment', {
        ...(replace === true ? { replace_draft_id: assessmentId } : {}),
        ...(startMode ? { start_mode: startMode } : {}),
      });
      if (actionRef.current !== token || token.generation !== loadGeneration.current || orgId !== selectedOrg?.id) return;
      await openAssessment(response.data.id, 'edit');
      if (actionRef.current === token) {
        setCurrentStep(0);
        requestAnimationFrame(() => levelHeadingRef.current?.focus());
        if (liveRef.current) liveRef.current.textContent = 'An assessment draft is ready';
      }
    } catch (e) {
      if (actionRef.current === token) {
        if (startMode) throw e;
        setActionError(e.message || 'Unable to start reassessment');
      }
    } finally { if (actionRef.current === token) { actionRef.current = null; if (token.generation === loadGeneration.current) setReassessing(false); } }
  }

  function currentAction(token) {
    return actionRef.current === token && token.generation === loadGeneration.current && token.orgId === selectedOrg?.id;
  }

  function answersPayload() {
    return Object.fromEntries(questions.filter(q => explicitResponses[q.id] || responseTouched[q.id]).map(q => [q.id, {
      checked: answers[q.id] || [],
      ...(answerModels[q.id] ? { model: answerModels[q.id] } : {}),
      ...(answerModels[q.id] === 'task_progress_v1' ? { tasks: taskProgress[q.id] } : {}),
      answer: deriveAnswer(q.id),
      ...(answerNotes[q.id] !== undefined ? { notes: answerNotes[q.id] } : {}),
      explicit: explicitResponses[q.id] === true,
    }]));
  }

  function queueSave(payload) {
    const generation = loadGeneration.current;
    const revision = ++saveRevision.current;
    const id = assessmentId;
    const owner = orgId;
    setDraftDirty(true); setSaving(true); setSaveError('');
    const request = saveTail.current.catch(() => {}).then(async () => {
      if (generation !== loadGeneration.current) return false;
      await base44.entities.Assessment.update(id, { answers: payload }, { headers: { 'X-Org-Id': owner } });
      return true;
    });
    saveTail.current = request;
    request.then(saved => {
      if (saved && revision === saveRevision.current && generation === loadGeneration.current) {
        setDraftDirty(false); setSaving(false);
      }
    }).catch(error => {
      if (revision === saveRevision.current && generation === loadGeneration.current) {
        setSaving(false); setSaveError(error.message || 'Could not save your progress');
      }
    });
    return request;
  }

  async function saveDraft() {
    if (!assessmentId || assessmentStatus !== 'in_progress') return true;
    try { return await queueSave(answersPayload()); } catch { return false; }
  }

  async function afterSaving(action) {
    if (actionRef.current) return;
    if (view !== 'edit' || !draftDirty) return action();
    const token = { generation: loadGeneration.current, orgId, assessmentId };
    actionRef.current = token; setTransitioning(true);
    const saved = await saveDraft();
    if (!currentAction(token)) return;
    actionRef.current = null; setTransitioning(false);
    if (saved) return action();
  }

  // Organisation changes do not navigate the router, so flush them separately.
  useEffect(() => registerOrganisationChangeGuard(async () => {
    if (actionRef.current) return false;
    if (!draftDirty) return true;
    const token = { generation: loadGeneration.current, orgId, assessmentId };
    actionRef.current = token; setTransitioning(true);
    const saved = await saveDraft();
    if (!currentAction(token)) return false;
    actionRef.current = null; setTransitioning(false);
    return saved;
  }));

  async function refreshDecisions() {
    if (actionRef.current || !assessmentId) return;
    const token = { generation: loadGeneration.current, orgId, assessmentId };
    actionRef.current = token; setRefreshing(true); setActionError(''); setActionAnnouncement('');
    try {
      const refreshed = await base44.entities.Assessment.get(assessmentId);
      if (!currentAction(token)) return;
      const savedAuths = refreshed.level_authorisations || {};
      setAuthorisations({
        1: savedAuths[1] || savedAuths['1'] || null,
        2: savedAuths[2] || savedAuths['2'] || null,
        3: savedAuths[3] || savedAuths['3'] || null,
      });
      setAssessmentStatus(refreshed.status);
      if (refreshed.status === 'completed') setResult(refreshed);
      const message = 'DI review checked';
      if (liveRef.current) liveRef.current.textContent = message;
      else setActionAnnouncement(message);
    }
    catch (e) { if (currentAction(token)) setActionError(e.message || 'Could not refresh provider decisions'); }
    finally { if (actionRef.current === token) { actionRef.current = null; setRefreshing(false); } }
  }

  function setTaskAnswer(qId, index, value) {
    if (actionRef.current) return;
    const question = questions.find(q => q.id === qId);
    const tasks = [...(taskProgress[qId] || assessmentTasks(question).map(task => (answers[qId] || []).includes(task) ? 'yes' : null))];
    tasks[index] = value;
    const next = { ...taskProgressAnswer(question, tasks), ...(answerNotes[qId] !== undefined ? { notes: answerNotes[qId] } : {}) };
    setTaskProgress(prev => ({ ...prev, [qId]: tasks }));
    setAnswerValues(prev => ({ ...prev, [qId]: next.answer }));
    setAnswers(prev => ({ ...prev, [qId]: next.checked }));
    setAnswerModels(prev => ({ ...prev, [qId]: next.model }));
    setLegacyQuestionIds(prev => prev.filter(id => id !== qId));
    setExplicitResponses(prev => ({ ...prev, [qId]: next.explicit }));
    setResponseTouched(prev => ({ ...prev, [qId]: true }));
    const generation = loadGeneration.current;
    setActionAnnouncement('');
    requestAnimationFrame(() => { if (generation === loadGeneration.current) setActionAnnouncement(`${assessmentTasks(question)[index]}: ${PROGRESS_LABELS[value]}. Question status: ${PROGRESS_LABELS[next.answer]}.`); });
    void queueSave({ ...answersPayload(), [qId]: next }).catch(() => {});
  }

  function deriveAnswer(qId) {
    const q = questions.find(qx => qx.id === qId);
    if (!q) return 'no';
    if (['self_report_v1', 'task_progress_v1'].includes(answerModels[qId]) || (q.sub_criteria?.length || 0) === 0) return answerValues[qId] || 'no';
    const total = q.sub_criteria?.length || 0;
    const checked = (answers[qId] || []).length;
    if (total > 0 && checked === total) return 'yes';
    if (checked > 0) return 'partial';
    return 'no';
  }

  function allCriteriaMetForLevel(lvl) {
    const qs = questions.filter(q => q.level === lvl);
    if (qs.length === 0) return false;
    return qs.every(q => {
      const total = q.sub_criteria?.length || 0;
      const checked = (answers[q.id] || []).length;
      return (assessmentStatus === 'completed' || explicitResponses[q.id]) && (['self_report_v1', 'task_progress_v1'].includes(answerModels[q.id]) || total === 0 ? answerValues[q.id] === 'yes' : checked === total);
    });
  }

  const [requestingLevel, setRequestingLevel] = useState(null);
  async function handleRequestAuthorisation(level) {
    if (!assessmentId || actionRef.current) return;
    const token = { generation: loadGeneration.current, orgId, assessmentId };
    actionRef.current = token; setRequestingLevel(level); setActionError(''); setActionAnnouncement('');
    try {
      if (assessmentStatus !== 'completed') {
        await saveTail.current.catch(() => {});
        if (!currentAction(token)) return;
        await base44.entities.Assessment.update(assessmentId, { answers: answersPayload() });
        if (!currentAction(token)) return;
        setDraftDirty(false); setSaveError('');
      }
      await base44.functions.invoke('requestLevelAuthorisation', { assessment_id: assessmentId, level });
      if (!currentAction(token)) return;
      const saved = await base44.entities.Assessment.get(assessmentId);
      if (!currentAction(token)) return;
      const savedAuths = saved.level_authorisations || {};
      setAuthorisations({
        1: savedAuths[1] || savedAuths['1'] || null,
        2: savedAuths[2] || savedAuths['2'] || null,
        3: savedAuths[3] || savedAuths['3'] || null,
      });
      if (saved.status === 'completed') setResult(saved);
      setActionAnnouncement('');
      requestAnimationFrame(() => document.getElementById(`level-${level}-authorisation-status`)?.focus());
    } catch (e) { if (currentAction(token)) setActionError(e.message || 'Could not request authorisation'); }
    finally { if (actionRef.current === token) { actionRef.current = null; if (token.generation === loadGeneration.current) { setRequestingLevel(null); } } }
  }

  const isAuthorised = (level) => authorisations[level]?.status === 'authorised';
  const questionsForLevel = questions.filter(q => q.level === currentLevel);

  async function handleSubmit() {
    if (!assessmentId || submitting || actionRef.current || assessmentStatus === 'completed') return;
    const firstUnanswered = questions.find(q => !explicitResponses[q.id]);
    if (firstUnanswered) {
      setCurrentStep(levels.indexOf(firstUnanswered.level));
      setActionError('Choose a status for every task before submitting. Not started, Partially completed and Completed are all valid choices.');
      setInvalidQuestionId(firstUnanswered.id);
      return;
    }
    const token = { generation: loadGeneration.current, orgId, assessmentId };
    actionRef.current = token; setSubmitting(true); setActionError('');
    try {
      await saveTail.current.catch(() => {});
      if (!currentAction(token)) return;
      const saved = await base44.entities.Assessment.update(assessmentId, { answers: answersPayload(), completed_at: new Date().toISOString() });
      if (currentAction(token)) { setDraftDirty(false); setSaveError(''); }
      if (!currentAction(token)) return;
      if (currentAction(token)) { setAssessmentStatus(saved.status); setTaskGeneration(saved.evidence_task_generation || { status: 'pending' }); setResult(saved); setView('review'); setAssessments(rows => [saved, ...rows.filter(row => row.id !== saved.id)]); }
    } catch (e) {
      if (!currentAction(token)) return;
      try {
        const recovered = await base44.entities.Assessment.get(assessmentId);
        if (!currentAction(token)) return;
        if (recovered.status === 'completed') {
          setDraftDirty(false); setSaveError(''); setAssessmentStatus(recovered.status);
          try { setTaskGeneration(await base44.functions.assessmentEvidenceTasks(recovered.id)); } catch { setTaskGeneration({ status: 'pending' }); }
          setResult(recovered); setView('review'); setAssessments(rows => [recovered, ...rows.filter(row => row.id !== recovered.id)]);
          return;
        }
      } catch { /* retain the original completion error */ }
      if (currentAction(token)) setActionError(e.message || 'Could not save assessment');
    }
    finally { if (actionRef.current === token) { actionRef.current = null; if (token.generation === loadGeneration.current) setSubmitting(false); } }
  }

  async function retryEvidenceTasks() {
    if (!result?.id || retryingTasks || actionRef.current) return;
    const token = { generation: loadGeneration.current, orgId, assessmentId: result.id };
    actionRef.current = token;
    setRetryingTasks(true); setActionError('');
    try {
      const status = await base44.functions.generateAssessmentEvidenceTasks(result.id);
      if (!currentAction(token)) return;
      setTaskGeneration(status);
      setActionAnnouncement(status.status === 'configuration_required'
        ? 'DI needs to publish a framework with evidence choices for every task. Your assessment is safely submitted.'
        : status.status === 'completed'
          ? status.claimed_count > 0 ? `${status.claimed_count} roadmap task${status.claimed_count === 1 ? ' is' : 's are'} ready` : 'No linked roadmap actions are currently available for this submission.'
          : status.paused ? 'Task creation is temporarily unavailable. Your answers and existing work are retained.' : 'Roadmap tasks still need to be created. Try again.');
    } catch (error) { if (currentAction(token)) setActionError(error.message || 'Evidence tasks could not be created. Try again.'); }
    finally { if (actionRef.current === token) { actionRef.current = null; if (token.generation === loadGeneration.current) setRetryingTasks(false); } }
  }

  function selectLevel(index) {
    setCurrentStep(index);
    requestAnimationFrame(() => {
      levelHeadingRef.current?.focus();
      levelHeadingRef.current?.scrollIntoView({ block: 'start' });
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" role="status" aria-live="polite">
        <div className="w-8 h-8 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" aria-label="Loading assessment" />
      </div>
    );
  }

  if (loadError) return <div className="assessment-panel p-8 max-w-xl mx-auto" role="alert"><h1 className="text-xl font-bold">Assessment unavailable</h1><p className="mt-2 text-muted-foreground">{loadError}</p><button className="assessment-primary mt-4 min-h-11 px-4 py-2 rounded bg-teal-700 text-white" onClick={() => loadQuestions(loadGeneration.current, lastLoad.current.requestedId, lastLoad.current.nextView)}>Retry</button>{reviewId && <button type="button" className="mt-4 min-h-11 px-4 py-2 underline" onClick={() => openAssessment()}>Assessment overview</button>}</div>;

  const submitted = assessments.filter(row => row.status === 'completed');
  const openDraft = assessments.find(row => row.status === 'in_progress');
  const dateLabel = row => new Date(row.completed_at || row.created_at || row.created_date).toLocaleString('en-GB');
  const buttonClass = 'min-h-11 rounded-lg border border-border bg-background px-4 py-2 text-foreground';
  const overview = () => afterSaving(() => openAssessment());
  const previousDrafts = assessments.filter(row => row.status === 'superseded');
  const leaveGuard = blocker.state === 'blocked' && saveError ? <ConfirmDialog title="Your progress has not been saved" description="Stay here and retry saving, or leave without your latest changes." action="Leave without saving" onClose={() => { if (!discardingNavigation.current && blocker.state === 'blocked') blocker.reset(); }} onConfirm={() => { discardingNavigation.current = true; setDraftDirty(false); blocker.proceed(); }} /> : null;
  const saveStatus = <>{saving && <p role="status">Saving progress…</p>}{saveError && <div role="alert"><p>{saveError}. Your changes are still on this page.</p><button type="button" className={buttonClass} onClick={saveDraft}>Retry save</button></div>}{leaveGuard}</>;
  const newAssessment = () => afterSaving(() => setReplacePrompt(true));
  const intro = <>
    <p>There are three sets of questions, one for each level of the Disability Confident scheme.</p>
    <p>Each question has a list of suggested tasks to help you complete your assessment.</p>
    <p>Use the buttons below to start an assessment, review saved answers or continue updating your progress.</p>
    <p>For each task, choose Not started, Partially completed or Completed. Your task status will automatically update the status of the question.</p>
    <p>Update continues your saved progress. When starting a new assessment, choose blank answers or carry forward compatible answers.</p>
    <div className="flex flex-wrap gap-3">
      <button ref={replaceTriggerRef} type="button" disabled={reassessing || submitting || transitioning} className={buttonClass} onClick={newAssessment}>Start a new assessment</button>
      <button type="button" disabled={!assessmentId || reassessing || submitting || transitioning} className={buttonClass} onClick={() => afterSaving(() => openAssessment(openDraft?.id || submitted[0]?.id, 'review'))}>Review your current answers</button>
      <button type="button" disabled={!assessmentId || reassessing || submitting || view === 'edit'} className={buttonClass} onClick={() => startReassessment()}>Update your answers as you make progress</button>
    </div>
    {!assessmentId && <p>Start your first assessment to enable review and updates.</p>}
    {replacePrompt && <StartAssessmentDialog returnFocusRef={replaceTriggerRef} replacingDraft={Boolean(openDraft)} canCarryForward={Boolean(openDraft || submitted.length)} canRefresh={!draftDirty} onRefresh={() => { setReplacePrompt(false); return openAssessment(); }} onClose={() => setReplacePrompt(false)} onConfirm={mode => startReassessment(Boolean(openDraft), mode)} />}
  </>;
  const reviewAnswers = (Heading = 'h3') => <ol className="space-y-4">{questions.map(q => <li key={q.id}><Heading className="font-semibold">Level {q.level}, question {q.order}: {q.question_text}</Heading><p>Question status: {answerValues[q.id] ? PROGRESS_LABELS[deriveAnswer(q.id)] : 'Not answered'}</p>{taskProgress[q.id] ? <ul className="list-disc pl-6">{assessmentTasks(q).map((task, i) => <li key={i}>{task}: {PROGRESS_LABELS[taskProgress[q.id][i]] || 'Not answered'}</li>)}</ul> : answerValues[q.id] && <p>Saved question answer. Individual task progress was not recorded.</p>}</li>)}</ol>;
  if (view === 'overview') return <div className="assessment-panel mx-auto max-w-3xl space-y-6">
    <h1 ref={overviewHeadingRef} data-route-focus tabIndex={-1} className="text-2xl font-bold">Maturity assessment</h1>
    {intro}{saveStatus}
    {actionError && <p role="alert" className="text-destructive">{actionError}</p>}
    {submitted[0] && <p>Latest submission: {dateLabel(submitted[0])}. Maturity score: {submitted[0].maturity_score}%.</p>}
    {previousDrafts.length > 0 && <section><h2 className="text-xl font-semibold">Saved previous drafts</h2><ul>{previousDrafts.map(row => <li key={row.id}><button type="button" className={buttonClass} onClick={() => openAssessment(row.id, 'review')}>Review saved draft, {dateLabel(row)}</button></li>)}</ul></section>}
    {submitted.length > 0 && <section><h2 className="text-xl font-semibold">Previous submissions</h2><p className="text-sm text-muted-foreground">Showing up to 100 recent assessment records.</p><ul className="mt-3 space-y-3">{submitted.map((row, i) => <li key={row.id}><button type="button" className={buttonClass} onClick={() => openAssessment(row.id, 'review')}>Review submission {submitted.length - i}, {dateLabel(row)}</button></li>)}</ul></section>}
  </div>;

  if (result?.status === 'superseded' || (view === 'review' && assessmentStatus === 'in_progress')) return <div className="assessment-panel mx-auto max-w-3xl space-y-4"><h1 ref={resultHeadingRef} data-route-focus tabIndex={-1}>{result?.status === 'superseded' ? 'Saved previous draft' : 'Review your current answers'}</h1><p>This is a read-only view. {result?.status === 'superseded' ? 'This previous draft was not submitted.' : 'Use Update your answers to make changes.'}</p><button type="button" className={buttonClass} onClick={overview}>Assessment overview</button>{!result && <button type="button" className={buttonClass} onClick={() => openAssessment(assessmentId, 'edit')}>Update your answers</button>}{reviewAnswers('h2')}{saveStatus}</div>;
  if (result) {
    return (
      <div className="assessment-panel mx-auto max-w-3xl space-y-6"><button type="button" className={buttonClass} onClick={overview}>Assessment overview</button><p>Submitted: {dateLabel(result)}</p><AssessmentResult
        result={result}
        headingRef={resultHeadingRef}
        onReassess={startReassessment}
        reassessing={reassessing}
        actionError={actionError}
        onViewRoadmap={() => navigate('/roadmap')}
        onRequestAuthorisation={handleRequestAuthorisation}
        onRefresh={refreshDecisions}
        requestingLevel={requestingLevel}
        refreshing={refreshing}
        eligibleLevels={Object.fromEntries(levels.map(level => [level, allCriteriaMetForLevel(level)]))}
        actionAnnouncement={actionAnnouncement}
        taskGeneration={taskGeneration}
        onRetryEvidenceTasks={retryEvidenceTasks}
        retryingTasks={retryingTasks}
      />
      <section><h2 className="text-xl font-semibold">Submitted answers</h2>{reviewAnswers()}</section>{saveStatus}</div>
    );
  }

  return (
    <div className="assessment-panel max-w-3xl mx-auto space-y-6">
      <div className="sr-only" ref={liveRef} aria-live="polite" aria-atomic="true" />
      <h1 data-route-focus tabIndex={-1} className="text-2xl font-bold">Maturity assessment</h1>
      {intro}
      <button type="button" onClick={overview} disabled={reassessing || submitting || transitioning} className={buttonClass}>Assessment overview</button>
      {saveStatus}
      {!saving && !saveError && responseTouched && Object.keys(responseTouched).length > 0 && <p className="text-sm">Progress saved automatically.</p>}
      {actionError && <p role="alert" className="text-destructive">{actionError}</p>}
      {actionAnnouncement && <p role="status">{actionAnnouncement}</p>}

      <details><summary className="min-h-11 cursor-pointer py-2 font-semibold">Jump to a question</summary><ol className="space-y-2">{questions.map(q => <li key={q.id}><button type="button" className="min-h-11 text-left underline" onClick={() => { setCurrentStep(levels.indexOf(q.level)); requestAnimationFrame(() => document.getElementById(`assessment-question-${q.id}`)?.querySelector('input')?.focus()); }}>Level {q.level}, question {q.order}: {q.question_text}</button></li>)}</ol></details>
      {Object.keys(responseTouched).length > 0 && <details><summary className="min-h-11 cursor-pointer py-2 font-semibold">Review changed answers ({Object.keys(responseTouched).length})</summary><p>Unchanged saved answers will be retained.</p><ul>{questions.filter(q => responseTouched[q.id]).map(q => <li key={q.id}>{q.question_text}: {deriveAnswer(q.id) === 'yes' ? 'Completed' : deriveAnswer(q.id) === 'partial' ? 'Partially completed' : 'Not started'}</li>)}</ul></details>}

      {/* Level tabs */}
      <nav aria-label="Assessment levels">
        <ol className="flex flex-wrap gap-2">
          {levels.map((lvl, idx) => (
            <li key={lvl} className="flex items-center gap-2">
              <button
                onClick={() => selectLevel(idx)}
                aria-current={idx === currentStep ? 'step' : undefined}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring ${
                  idx === currentStep ? 'bg-teal-700 text-white' :
                  'bg-teal-100 text-teal-700 hover:bg-teal-200'
                }`}
              >
                <span aria-hidden="true" className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold border-2 border-current">
                  {isAuthorised(lvl) ? '✓' : lvl}
                </span>
                Level {lvl} — {LEVEL_LABELS[lvl]}
              </button>
              {idx < 2 && <ChevronRight className="hidden sm:block w-4 h-4 text-muted-foreground" aria-hidden="true" />}
            </li>
          ))}
        </ol>
      </nav>

      {/* Authorisation status summary */}
      <div className="flex flex-wrap items-center gap-4 text-xs">
        {[1, 2, 3].map(l => (
          <span key={l} className={`flex items-center gap-1 ${isAuthorised(l) ? 'assessment-accent font-semibold' : 'text-muted-foreground'}`}>
            {isAuthorised(l) ? <ShieldCheck className="w-3.5 h-3.5" aria-hidden="true" /> : <Circle className="w-3.5 h-3.5" aria-hidden="true" />}
            L{l} {isAuthorised(l) ? 'Authorised' : authorisations[l]?.status === 'requested' ? 'Requested' : authorisations[l]?.status === 'returned' ? 'Returned' : 'Pending'}
          </span>
        ))}
      </div>

      {/* Questions */}
      <div className="space-y-4">
        <h2 ref={levelHeadingRef} tabIndex={-1} className="text-xl font-bold text-foreground">Level {currentLevel} — {LEVEL_LABELS[currentLevel]} questions</h2>
        {questionsForLevel.reduce((groups, q) => {
          if (groups.at(-1)?.theme !== q.theme) groups.push({ theme: q.theme, questions: [] });
          groups.at(-1).questions.push(q);
          return groups;
        }, []).map(({ theme, questions: qs }, groupIndex) => (
          <div key={`${theme}-${groupIndex}`} className="bg-card border border-border rounded-xl p-5 shadow-sm">
            {theme && !qs.every(q => q.question_text.trim() === theme.trim()) && <p className="assessment-accent text-sm font-semibold mb-4">{theme}</p>}
            <div className="space-y-6">
              {qs.map(q => (
                <QuestionCard
                  key={q.id}
                  question={q}
                  answer={deriveAnswer(q.id)}
                  answered={Boolean(explicitResponses[q.id])}
                  needsConfirmation={legacyQuestionIds.includes(q.id)}
                  tasks={taskProgress[q.id]}
                  onTaskAnswer={setTaskAnswer}
                  disabled={Boolean(actionRef.current) || refreshing || transitioning}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Level Authorisation */}
      <LevelAuthorisation
        level={currentLevel}
        levelLabel={LEVEL_LABELS[currentLevel]}
        allCriteriaMet={allCriteriaMetForLevel(currentLevel)}
        status={authorisations[currentLevel]?.status || 'none'}
        notes={authorisations[currentLevel]?.notes}
        requesting={requestingLevel === currentLevel || Boolean(actionRef.current) || refreshing}
        onRequest={() => handleRequestAuthorisation(currentLevel)}
        questionCount={questionsForLevel.length}
      />

      {/* Navigation */}
      <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
        <button
          onClick={() => selectLevel(Math.max(0, currentStep - 1))}
          disabled={currentStep === 0}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium border border-border hover:bg-muted disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring"
        >
          <ChevronLeft className="w-4 h-4" aria-hidden="true" />
          Previous
        </button>

        {currentStep < 2 && (
          <button
            onClick={() => selectLevel(currentStep + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold assessment-primary bg-teal-700 text-white hover:bg-teal-800 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
          >
            Next Level
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold assessment-primary bg-teal-700 text-white hover:bg-teal-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400"
          >
            {submitting ? 'Submitting…' : submitted.length ? 'Submit updated assessment' : 'Submit Assessment'}
            <CheckCircle className="w-4 h-4" aria-hidden="true" />
          </button>
      </div>

      {currentStep === 2 && <p className="text-xs text-muted-foreground text-center">Submitting records your self-assessment. DI authorisation is a separate review available for fully achieved levels.</p>}
    </div>
  );
}

function AssessmentResult({ result, headingRef, onReassess, onViewRoadmap, reassessing, actionError, onRequestAuthorisation, onRefresh, requestingLevel, refreshing, eligibleLevels, actionAnnouncement, taskGeneration, onRetryEvidenceTasks, retryingTasks }) {
  const levelLabels = { 0: 'Not Yet Achieved', 1: 'Committed', 2: 'Employer', 3: 'Leader' };
  const levelColors = { 0: 'text-foreground', 1: 'text-foreground', 2: 'assessment-accent', 3: 'assessment-accent' };
  const levelAuths = result.level_authorisations || {};

  return (
    <div className="assessment-panel max-w-2xl mx-auto space-y-6" role="region" aria-labelledby="result-heading">
      <div className="sr-only" aria-live="polite" aria-atomic="true">{actionAnnouncement}</div>
      <div className="bg-card border border-border rounded-2xl p-8 shadow-sm text-center">
        <div className="w-16 h-16 rounded-2xl bg-teal-100 flex items-center justify-center mx-auto mb-4" aria-hidden="true">
          <CheckCircle className="w-8 h-8 text-teal-600" />
        </div>
        <h1 ref={headingRef} id="result-heading" data-route-focus tabIndex={-1} className="text-2xl font-bold text-foreground mb-1">Self-assessment submitted</h1>
        <p className={`text-4xl font-extrabold mt-4 ${levelColors[result.current_level]}`}>
          Level {result.current_level} — {levelLabels[result.current_level]}
        </p>
        <p className="text-base text-muted-foreground mt-1">Overall Maturity Score: <strong className="text-foreground">{result.maturity_score}%</strong></p>

        <button type="button" onClick={onReassess} disabled={!onReassess || reassessing} className="assessment-primary mt-5 inline-flex min-h-11 items-center gap-2 rounded-lg bg-teal-700 px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-50">
          <RotateCcw className="h-4 w-4" aria-hidden="true" />
          {reassessing ? 'Starting reassessment…' : 'Update your answers'}
        </button>

        <div className="grid grid-cols-3 gap-4 mt-6">
          {[['Level 1', result.level1_score, 'text-foreground'], ['Level 2', result.level2_score, 'assessment-accent'], ['Level 3', result.level3_score, 'assessment-accent']].map(([label, score, color]) => (
            <div key={label} className="bg-muted rounded-xl p-3">
              <p className={`text-xl font-bold ${color}`}>{score}%</p>
              <p className="text-xs text-muted-foreground">{label}</p>
            </div>
          ))}
        </div>
      </div>

      {taskGeneration?.status === 'configuration_required' ? <p role="status" className="rounded-xl border border-border bg-card p-4 text-sm text-foreground"><strong>Your assessment is safely submitted.</strong> DI needs to publish a framework with evidence choices for every task. Then start a new assessment using that version. Your submitted answers and existing roadmap work are retained.</p> : taskGeneration?.status === 'pending' ? <div role="status" className="rounded-xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900"><p><strong>Your assessment is safely submitted.</strong> Its roadmap tasks still need to be created.</p>{taskGeneration.paused ? <p className="mt-2">Task creation is temporarily unavailable. Your answers and existing work are retained. Return later to create its roadmap tasks.</p> : <button type="button" onClick={onRetryEvidenceTasks} disabled={retryingTasks} className="mt-2 min-h-11 rounded-lg border border-amber-700 px-3 py-2 font-semibold">{retryingTasks ? 'Creating tasks…' : 'Retry roadmap tasks'}</button>}</div> : taskGeneration?.status === 'completed' ? <p role="status" className="rounded-xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900">{taskGeneration.claimed_count > 0 ? `${taskGeneration.claimed_count} roadmap task${taskGeneration.claimed_count === 1 ? ' is' : 's are'} ready on your roadmap.` : 'No linked roadmap actions are currently available for this submission.'}</p> : null}

      {/* Level authorisations */}
      <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-foreground mb-3">DI review</h2><p className="mb-3 text-sm">Diverse Integration reviews your evidence separately from your self-assessment. Check for an update after requesting a review.</p>
        <div className="space-y-2">
          {[1, 2, 3].map(l => {
            const auth = levelAuths[l] || levelAuths[String(l)];
            return (
              <div key={l} id={`level-${l}-authorisation-status`} tabIndex={-1} className="flex flex-wrap items-center gap-2 text-sm">
                {auth?.status === 'authorised' ? (
                  <>
                    <ShieldCheck className="w-4 h-4 text-teal-700 shrink-0" aria-hidden="true" />
                    <span className="text-foreground">Level {l} — {levelLabels[l]}: Authorised by {auth.reviewed_by || auth.by || 'Diverse Integration'}</span>
                  </>
                ) : auth?.status === 'requested' ? (
                  <>
                    <Clock className="w-4 h-4 text-sky-600 shrink-0" aria-hidden="true" />
                    <span className="text-sky-700">Level {l} — {levelLabels[l]}: Requested</span>
                  </>
                ) : auth?.status === 'returned' ? (
                  <>
                    <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" aria-hidden="true" />
                    <span className="text-amber-700">Level {l} — {levelLabels[l]}: Returned{auth.notes ? ` — ${auth.notes}` : ''}</span>
                  </>
                ) : (
                  <>
                    <Circle className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                    <span className="text-muted-foreground">Level {l} — {levelLabels[l]}: Not authorised</span>
                  </>
                )}
                {auth?.status !== 'authorised' && auth?.status !== 'requested' && eligibleLevels[l] && <button type="button" onClick={() => onRequestAuthorisation(l)} disabled={Boolean(requestingLevel) || refreshing} className="ml-auto min-h-11 rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white disabled:opacity-50">{requestingLevel === l ? 'Requesting…' : auth?.status === 'returned' ? `Request Level ${l} review again` : `Request Level ${l} DI authorisation`}</button>}
              </div>
            );
          })}
        </div>
      </div>

      {result.risk_flags?.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-5" role="alert">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" aria-hidden="true" />
            <div>
              <h2 className="text-sm font-semibold text-amber-800">Risk Flags</h2>
              <ul className="mt-2 space-y-1">
                {result.risk_flags.map((f, i) => <li key={i} className="text-sm text-amber-700">• {f}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      {result.gaps?.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 shadow-sm">
          <h2 className="text-sm font-semibold text-foreground mb-3">Key Gaps Identified ({result.gaps.length})</h2>
          <ul className="space-y-1.5">
            {result.gaps.slice(0, 8).map((g, i) => (
              <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                <span className="text-amber-500 shrink-0 mt-0.5" aria-hidden="true">•</span>
                {g}
              </li>
            ))}
            {result.gaps.length > 8 && <li className="text-xs text-muted-foreground">...and {result.gaps.length - 8} more — view your roadmap for the full list.</li>}
          </ul>
        </div>
      )}

      {actionError && <p role="alert" className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">{actionError}</p>}
      <div className="flex flex-wrap gap-3">
        <button type="button" onClick={onRefresh} disabled={refreshing || Boolean(requestingLevel)} className="min-h-11 rounded-lg border border-border px-4 py-2.5 text-sm font-medium">{refreshing ? 'Checking…' : 'Check for an update from DI'}</button>
        <button onClick={onViewRoadmap} className="assessment-primary flex-1 flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-teal-700 text-white text-sm font-semibold hover:bg-teal-700 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal-400">
          View Your Roadmap
          <ChevronRight className="w-4 h-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}
